import { useState } from "react";
import { X, Plus, Minus, Type, Eye, MousePointer, Image as ImageIcon, AlignLeft, Pause } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";

export function AccessibilityWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [fontSize, setFontSize] = useState(100);
  const [highContrast, setHighContrast] = useState(false);
  const [grayscale, setGrayscale] = useState(false);
  const [dyslexiaFont, setDyslexiaFont] = useState(false);
  const [highlightLinks, setHighlightLinks] = useState(false);
  const [largeCursor, setLargeCursor] = useState(false);
  const [lineHeight, setLineHeight] = useState(100);
  const [letterSpacing, setLetterSpacing] = useState(100);
  const [hideImages, setHideImages] = useState(false);
  const [pauseAnimations, setPauseAnimations] = useState(false);

  const applyAccessibilitySettings = () => {
    const root = document.documentElement;
    
    // Font size
    root.style.fontSize = `${fontSize}%`;
    
    // High contrast
    if (highContrast) {
      root.classList.add("accessibility-high-contrast");
    } else {
      root.classList.remove("accessibility-high-contrast");
    }
    
    // Grayscale
    if (grayscale) {
      root.classList.add("accessibility-grayscale");
    } else {
      root.classList.remove("accessibility-grayscale");
    }
    
    // Dyslexia font
    if (dyslexiaFont) {
      root.classList.add("accessibility-dyslexia-font");
    } else {
      root.classList.remove("accessibility-dyslexia-font");
    }
    
    // Highlight links
    if (highlightLinks) {
      root.classList.add("accessibility-highlight-links");
    } else {
      root.classList.remove("accessibility-highlight-links");
    }
    
    // Large cursor
    if (largeCursor) {
      root.classList.add("accessibility-large-cursor");
    } else {
      root.classList.remove("accessibility-large-cursor");
    }
    
    // Line height
    root.style.setProperty("--accessibility-line-height", `${lineHeight / 100}`);
    
    // Letter spacing
    root.style.setProperty("--accessibility-letter-spacing", `${(letterSpacing - 100) / 100}em`);
    
    // Hide images
    if (hideImages) {
      root.classList.add("accessibility-hide-images");
    } else {
      root.classList.remove("accessibility-hide-images");
    }
    
    // Pause animations
    if (pauseAnimations) {
      root.classList.add("accessibility-pause-animations");
    } else {
      root.classList.remove("accessibility-pause-animations");
    }
  };

  const resetAll = () => {
    setFontSize(100);
    setHighContrast(false);
    setGrayscale(false);
    setDyslexiaFont(false);
    setHighlightLinks(false);
    setLargeCursor(false);
    setLineHeight(100);
    setLetterSpacing(100);
    setHideImages(false);
    setPauseAnimations(false);
    
    const root = document.documentElement;
    root.style.fontSize = "";
    root.style.removeProperty("--accessibility-line-height");
    root.style.removeProperty("--accessibility-letter-spacing");
    root.classList.remove(
      "accessibility-high-contrast",
      "accessibility-grayscale",
      "accessibility-dyslexia-font",
      "accessibility-highlight-links",
      "accessibility-large-cursor",
      "accessibility-hide-images",
      "accessibility-pause-animations"
    );
  };

  // Apply settings whenever they change
  useState(() => {
    applyAccessibilitySettings();
  });

  const handleFontSizeChange = (delta: number) => {
    const newSize = Math.max(75, Math.min(200, fontSize + delta));
    setFontSize(newSize);
    setTimeout(applyAccessibilitySettings, 0);
  };

  const handleLineHeightChange = (delta: number) => {
    const newHeight = Math.max(100, Math.min(200, lineHeight + delta));
    setLineHeight(newHeight);
    setTimeout(applyAccessibilitySettings, 0);
  };

  const handleLetterSpacingChange = (delta: number) => {
    const newSpacing = Math.max(100, Math.min(150, letterSpacing + delta));
    setLetterSpacing(newSpacing);
    setTimeout(applyAccessibilitySettings, 0);
  };

  const handleToggle = (setter: (value: boolean) => void, currentValue: boolean) => {
    setter(!currentValue);
    setTimeout(applyAccessibilitySettings, 0);
  };

  return (
    <>
      {/* Floating Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-32 right-4 sm:right-8 z-[9999] w-14 h-14 rounded-full bg-gradient-to-r from-emerald-400 to-teal-500 text-slate-950 shadow-[0_8px_30px_rgba(16,185,129,0.4)] hover:shadow-[0_12px_40px_rgba(16,185,129,0.6)] hover:-translate-y-1 transition-all duration-300 flex items-center justify-center text-2xl font-bold touch-manipulation"
        aria-label="Open accessibility settings"
        title="Accessibility settings"
      >
        ♿
      </button>

      {/* Accessibility Panel */}
      {isOpen && (
        <div className="fixed bottom-32 right-4 sm:right-20 z-[9998] w-80 max-h-[70vh] bg-slate-900/95 backdrop-blur-xl border border-emerald-400/30 rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.4)] overflow-hidden">
          <div className="p-4 bg-gradient-to-r from-emerald-400/10 to-teal-500/10 border-b border-emerald-400/20 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-2xl">♿</span>
              <h3 className="text-lg font-semibold text-emerald-100">Accessibility</h3>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsOpen(false)}
              className="text-slate-300 hover:text-white hover:bg-slate-800/50"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          <ScrollArea className="h-[calc(70vh-80px)]">
            <div className="p-4 space-y-4">
              {/* Text Size */}
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium text-slate-200">
                  <Type className="h-4 w-4" />
                  <span>Text size: {fontSize}%</span>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleFontSizeChange(-10)}
                    className="flex-1 border-emerald-400/30 text-slate-200"
                  >
                    <Minus className="h-4 w-4 mr-1" /> Smaller
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleFontSizeChange(10)}
                    className="flex-1 border-emerald-400/30 text-slate-200"
                  >
                    <Plus className="h-4 w-4 mr-1" /> Larger
                  </Button>
                </div>
              </div>

              <Separator className="bg-slate-700/50" />

              {/* Line Height */}
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium text-slate-200">
                  <AlignLeft className="h-4 w-4" />
                  <span>Line height: {lineHeight}%</span>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleLineHeightChange(-10)}
                    className="flex-1 border-emerald-400/30 text-slate-200"
                  >
                    <Minus className="h-4 w-4 mr-1" /> Less
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleLineHeightChange(10)}
                    className="flex-1 border-emerald-400/30 text-slate-200"
                  >
                    <Plus className="h-4 w-4 mr-1" /> More
                  </Button>
                </div>
              </div>

              <Separator className="bg-slate-700/50" />

              {/* Letter Spacing */}
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium text-slate-200">
                  <Type className="h-4 w-4" />
                  <span>Letter spacing: {letterSpacing}%</span>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleLetterSpacingChange(-10)}
                    className="flex-1 border-emerald-400/30 text-slate-200"
                  >
                    <Minus className="h-4 w-4 mr-1" /> Less
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleLetterSpacingChange(10)}
                    className="flex-1 border-emerald-400/30 text-slate-200"
                  >
                    <Plus className="h-4 w-4 mr-1" /> More
                  </Button>
                </div>
              </div>

              <Separator className="bg-slate-700/50" />

              {/* Vision Adjustments */}
              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-emerald-200 flex items-center gap-2">
                  <Eye className="h-4 w-4" />
                  Vision
                </h4>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-300">High contrast</span>
                  <Switch
                    checked={highContrast}
                    onCheckedChange={() => handleToggle(setHighContrast, highContrast)}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-300">Grayscale</span>
                  <Switch
                    checked={grayscale}
                    onCheckedChange={() => handleToggle(setGrayscale, grayscale)}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-300">Highlight links</span>
                  <Switch
                    checked={highlightLinks}
                    onCheckedChange={() => handleToggle(setHighlightLinks, highlightLinks)}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-300">Hide images</span>
                  <Switch
                    checked={hideImages}
                    onCheckedChange={() => handleToggle(setHideImages, hideImages)}
                  />
                </div>
              </div>

              <Separator className="bg-slate-700/50" />

              {/* Reading Assistance */}
              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-emerald-200 flex items-center gap-2">
                  <Type className="h-4 w-4" />
                  Reading
                </h4>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-300">Dyslexia-friendly font</span>
                  <Switch
                    checked={dyslexiaFont}
                    onCheckedChange={() => handleToggle(setDyslexiaFont, dyslexiaFont)}
                  />
                </div>
              </div>

              <Separator className="bg-slate-700/50" />

              {/* Navigation */}
              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-emerald-200 flex items-center gap-2">
                  <MousePointer className="h-4 w-4" />
                  Navigation
                </h4>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-300">Large cursor</span>
                  <Switch
                    checked={largeCursor}
                    onCheckedChange={() => handleToggle(setLargeCursor, largeCursor)}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-300">Pause animations</span>
                  <Switch
                    checked={pauseAnimations}
                    onCheckedChange={() => handleToggle(setPauseAnimations, pauseAnimations)}
                  />
                </div>
              </div>

              <Separator className="bg-slate-700/50" />

              {/* Reset Button */}
              <Button
                onClick={resetAll}
                variant="outline"
                className="w-full border-red-400/30 text-red-300 hover:bg-red-500/10"
              >
                Reset all settings
              </Button>

              <div className="text-xs text-slate-400 pt-2 space-y-1">
                <p>💡 Tip: Use Tab key to navigate, Enter to select</p>
                <p>🔊 For screen reader support, use your browser's built-in tools</p>
              </div>
            </div>
          </ScrollArea>
        </div>
      )}
    </>
  );
}
